import { Ref, reactive, onUnmounted, ComponentInternalInstance, getCurrentInstance } from '@vue/composition-api';
import { DialogPlugin } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import ipc from '@/renderer/scripts/utils/ipc';
import { loopAsyncTask } from '@/renderer/scripts/utils/syncTask';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

export type DeleteItem = {
  type: SMH.FileItemType;
  path: string[];
  spaceOrgId?: number;
  virusAuditStatus?: number;
  sensitiveWordAuditStatus?: number;
};
type FailInfo = {
  list: unknown[];
  type: string;
};
type Handler = {
  methods: {
    confirmDelete: (deleteData: DeleteItem[], fromShare?: boolean, closeCallBack?: Function) => void;
    onDelete: (deleteData: DeleteItem[]) => Promise<void>;
  };
};
export default function Handler(spaceId: Ref<SpaceId>, fromFavor?: boolean): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const { $route, $store } = currComponent.proxy;
  const { editionFlag } = $store.getters;
  const failInfo = reactive<FailInfo>({
    list: [],
    type: 'delete',
  });

  // 为了给重试的时候补充数据的
  let preDeleteData: Array<SMH.BatchDeleteItem> = [];
  const methods = {
    confirmDelete(deleteData: DeleteItem[], fromShare = false, closeCallBack?: Function) {
      let time = 0;
      switch (editionFlag) {
        case 'personal':
          time = 3;
          break;
        default:
          time = 90;
      }
      let delTip: any = i18n_t('确定要删除选中的项目吗？项目删除后{time}天内可以在误删恢复中找回。', {
        time,
      });
      if ($route.name === 'relatedToMeFileList' || fromShare) {
        delTip = i18n_t('是否删除该文件？删除后将放入共享发出者的误删恢复');
      } else if ($route.name === 'collaborationSpaceList') {
        delTip = (h: any) =>
          h('div', [
            h('h2', i18n_t('确认要删除群组文件吗？')),
            h('p', i18n_t('文件将被移至误删恢复，您可以从误删恢复中找回')),
          ]);
      }
      let separateNum = 0;
      deleteData.forEach(item => {
        (item.virusAuditStatus === 3 || item.sensitiveWordAuditStatus === 2) && separateNum++;
      });
      if (separateNum === deleteData.length) {
        delTip = i18n_t('确定要删除选中的项目吗？选择“可疑文件”将会被彻底删除，无法恢复。');
      } else if (separateNum && separateNum < deleteData.length) {
        delTip = i18n_t(
          '确定要删除选中的项目吗？项目删除后90天内可以在误删恢复中找到。选择文件包含“可疑文件”将会被彻底删除，无法恢复。',
        );
      }
      const deleteDialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: delTip,
        theme: 'warning',
        onConfirm: () => {
          this.onDelete(deleteData);
          if (deleteDialog.hide) {
            deleteDialog.hide();
          }
        },
        onClosed: () => {
          if (closeCallBack) {
            closeCallBack();
          }
        },
      });
    },
    async onDelete(deleteData: DeleteItem[]) {
      try {
        tips.loading(i18n_t('正在删除'), 0);
        let res;
        if (deleteData.length > 1) {
          const deleteList: Array<SMH.BatchDeleteItem> = [];
          deleteData.forEach(ele => {
            const path = ele.path.join('/');
            if (ele.virusAuditStatus === 3 || ele.sensitiveWordAuditStatus === 2) {
              deleteList.push({
                path,
                permanent: true,
              });
            } else {
              deleteList.push({
                path,
              });
            }
          });
          preDeleteData.push(...deleteList);
          preDeleteData = [...new Set(preDeleteData)];
          await methods.batchDeleteMethods(deleteList);
        } else {
          const spaceOrgId = deleteData[0].spaceOrgId ? deleteData[0].spaceOrgId : $store.getters.orgId;
          const deletePath = deleteData[0].path.join('/');
          res =
            deleteData[0].type === 'dir'
              ? await smh.commonAPI.deleteDirectory({
                  dirPath: deletePath,
                  spaceId: spaceId.value,
                  spaceOrgId: +spaceOrgId,
                })
              : await smh.commonAPI.deleteFile({
                  filePath: deletePath,
                  spaceId: spaceId.value,
                  spaceOrgId: +spaceOrgId,
                  permanent: Number(
                    deleteData[0].virusAuditStatus === 3 || deleteData[0].sensitiveWordAuditStatus === 2,
                  ),
                });
          tips.success(i18n_t('删除成功'));
        }
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH, deleteData, 'delete');
        fromFavor && ipc.emit(ipc.RENDERER_EVENTS_MAP.FAVOR_LIST_FRESH);
      } catch (error: MsgError) {
        if (error.toString() === 'Error: [SMH Warning]: Logout') {
          tips.error(i18n_t('登出导致删除失败，请登录后重试'));
        } else {
          tips.error(error as MsgError);
        }
      }
    },
    async batchDeleteMethods(batchList: Array<SMH.BatchDeleteItem>) {
      const res = await smh.commonAPI.batchDelete(
        {
          spaceId: spaceId.value,
        },
        {
          body: batchList,
        },
      );
      if (res.status === 200) {
        tips.success(i18n_t('删除成功'));
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ERR_LIST_CLEAR);
      } else if (res.status === 202) {
        const result = await loopAsyncTask(smh.commonAPI.asyncTaskQuery, res.data.taskId, spaceId.value);
        if (result.status === 200) {
          tips.success(i18n_t('删除成功'));
        } else if (result.status === 207) {
          methods.handleBatchResult(result.result);
        }
      } else {
        methods.handleBatchResult(res.data.result);
      }
    },
    handleBatchResult(resData: Array<any>) {
      failInfo.list = [];
      resData.forEach((item: any, i: number) => {
        if (item.status !== 200 && item.status !== 204) {
          failInfo.list.push({
            path: item.path.join('/'),
            msg: `${formatError(item.code, true)}`,
            id: i,
          });
        }
      });
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ERRINFO_SHOW, failInfo);
    },
    async onDeleteRetry(data: Array<SMH.BatchDeleteItem>) {
      try {
        if (data.length === 1) {
          let permanent = true;
          for (let i = 0; i < preDeleteData.length; i++) {
            if (preDeleteData[i].path === data[0].path) {
              permanent = Boolean(preDeleteData[i].permanent);
              break;
            }
          }
          await smh.commonAPI.deleteFile({
            filePath: data[0].path,
            spaceId: spaceId.value,
            permanent: Number(permanent),
          });
          tips.success(i18n_t('删除成功'));
          ipc.emit(ipc.RENDERER_EVENTS_MAP.ERR_LIST_DEL, data[0].id);
        } else {
          for (let i = 0; i < preDeleteData.length; i++) {
            for (let j = 0; j < data.length; j++) {
              if (preDeleteData[i].path === data[j].path) data[j].permanent = Boolean(preDeleteData[i].permanent);
            }
          }
          await methods.batchDeleteMethods(data);
        }
      } catch (error) {
        tips.error(error as MsgError);
      }
    },
  };
  const retryDelete = (data: Array<SMH.BatchDeleteItem>) => {
    methods.onDeleteRetry(data);
  };
  ipc.on(ipc.RENDERER_EVENTS_MAP.RETRY_DELETE, retryDelete);
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.RETRY_DELETE, retryDelete);
  });
  return {
    methods,
  };
}
